<template>
    <div class="google-pay-container" v-if="hasClientData && paymentRequest">
        <google-pay-button
            :environment="googlePayData.environment"
            :button-color="buttonColor"
            :button-type="buttonType"
            button-size-mode="fill"
            :paymentRequest.prop="paymentRequest"
            @loadpaymentdata="onLoadPaymentData"
            @error="onError"
            :style="{ width: `${buttonWidth}`, height: `${buttonHeight}`, borderRadius: '8px' }"
        ></google-pay-button>
    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import "@google-pay/button-element";
import {DateTime} from "luxon";
import ApiRequest from "../../../../utils/api-request";
import CartMixin from "../../../../mixins/CartMixin";
import LoaderMixin from "../../../../mixins/LoaderMixin";

export default {
    name: 'GooglePay',
    mixins: [CartMixin, LoaderMixin],
    props: {
        amount: {
            type: Number,
            default: 0
        },
        refContactInformation: {
            type: Object,
            default: null
        },
        referrer: {
            type: String,
            default: ''
        },
        isProduct: {
            type: Boolean,
            default: false
        },
        productShippingAddress: {
            type: String,
            default: ''
        },
        productVariantSelected: {
            type: String,
            default: ''
        },
        productShippingMethod: {
            type: String,
            default: ''
        },
        product: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            payload: {},
            order_id: 0,
            currency: 'USD',
            hasClientData: true,
            productData: {
                isProduct: this.isProduct,
                referrer: this.referrer,
                productShippingMethod: this.productShippingMethod,
                productShippingAddress: this.productShippingAddress,
                productVariantSelected: this.productVariantSelected
            },

            buttonColor: "default",
            buttonType: "buy",
            isCustomSize: false,
            buttonWidth: '100%',
            buttonHeight: '55px',
            paymentRequest: null,
        }
    },

    computed: {
        ...mapGetters('app', ['firstName', 'lastName', 'address2', 'email', 'phone']),
        ...mapGetters('app', ['googlePayData']),
        ...mapGetters('filter', ['journeyDate']),
        ...mapGetters('cart', ['isEmpty', 'totalPrice', 'totalTax', 'totalDiscount']),
        ...mapGetters('cart', {
            usedPromoCode: 'promoCode',
            cartBikes: 'bikes',
            cartEvents: 'events',
            cartTours: 'tours',
            cartAccessories: 'accessories',
            cartMerchandises: 'merchandises',
            cartAdditionalOptions: 'additionalOptions'
        }),
    },

    mounted() {
        if(!this.googlePayData.merchantId && !this.googlePayData.merchantName && !this.googlePayData.gateway && !this.googlePayData.gatewayMerchantId) {
            this.hasClientData = false;
        } else {
            this.createPaymentRequest(this.amount)
        }
    },

    methods: {
        ...mapActions('cart', ['usePromoCode']),
        ...mapActions('payment', ['initCreditCardPayment']),
        errorNotify(message) {
            if (message) {
                this.$notify({
                    text: message,
                    type: 'error'
                });
            } else {
                this.$notify({
                    type: 'error',
                    title: 'Server error!',
                    text: 'Something went wrong. Please contact our support service',
                    duration: 5000
                });
            }
        },

        createPaymentRequest(amount) {
            this.paymentRequest = {
                apiVersion: 2,
                apiVersionMinor: 0,
                allowedPaymentMethods: [
                    {
                        type: "CARD",
                        parameters: {
                            allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                            allowedCardNetworks: ['AMEX', 'DISCOVER', 'JCB', 'MASTERCARD', 'VISA']
                        },
                        tokenizationSpecification: {
                            type: "PAYMENT_GATEWAY",
                            parameters: {
                                gateway: this.googlePayData.gateway,
                                gatewayMerchantId: this.googlePayData.gatewayMerchantId
                            }
                        }
                    }
                ],
                merchantInfo: {
                    merchantId: this.googlePayData.merchantId,
                    merchantName: this.googlePayData.merchantName
                },
                transactionInfo: {
                    totalPriceStatus: "FINAL",
                    totalPriceLabel: "Total",
                    totalPrice: parseFloat(amount).toFixed(2),
                    currencyCode: "USD",
                    countryCode: "US"
                }
            }
        },

        // checkoutAmount() {
        //     const payload = this.createPayload('google-pay', this.productData)
        //     new ApiRequest().checkoutAmount(payload).then((response) => {
        //
        //         if(response.data.status) {
        //             const amount = response.data.card.total ? parseInt(response.data.card.total)/100 : 0;
        //
        //             if(amount) {
        //                 this.createPaymentRequest(amount)
        //             }
        //         } else {
        //             this.errorNotify(response.data.message);
        //         }
        //     }).catch((error) => {
        //         console.error(error);
        //
        //     }).finally(() => {
        //         this.disableLoader();
        //     });
        // },

        createPayloadS() {
            // payment's main fields
            let payload = {
                debug: process.env.NODE_ENV === 'development',
                ref: this.referrer,
                discount_code: this.usedPromoCode ? this.usedPromoCode.code : null,
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                phone: this.phone,
                address: this.address2.firstLine,
                postalCode: this.address2.zipCode,
                paymentType: 'google-pay',
                paymentToken: ''
            }

            // this part of JSON data object is common
            let data = {
                accessories: this.cartAccessories.map(i => ({
                    accessory_id: i.item.accessory_id,
                    price_id: i.priceOption.price_id,
                    qty: i.amount
                })),
                merchandises: this.cartMerchandises.map(i => ({
                    merchandise_id: i.item.id,
                    size: i.priceOption.size,
                    qty: i.amount
                })),
                additional_options: this.cartAdditionalOptions.map(i => ({
                    additional_option_id: i.item.id,
                    qty: i.amount
                }))
            }

            // structure for rentals checkout
            if (this.cartBikes.length && !this.isProduct) {
                payload = {
                    ...payload,
                    start: DateTime.fromISO(this.journeyDate).toFormat('yyyy-MM-dd') + " 00:00:00",
                    type: 'rental',
                    data: JSON.stringify({
                        ...data,
                        bikes: this.cartBikes.map(i => ({
                            type_id: i.item.type_id,
                            price_id: i.priceOption.price_id,
                            qty: i.amount,
                            insurance_qty: i.insuranceAmount
                        }))
                    })
                }
            }

            // structure for rentals checkout
            if (this.cartEvents.length && !this.isProduct) {
                payload = {
                    ...payload,
                    start: DateTime.fromISO(this.journeyDate).toFormat('yyyy-MM-dd') + " 00:00:00",
                    type: 'event',
                    data: JSON.stringify({
                        ...data,
                        event_id: this.cartEvents[0].item.event_id,
                        bikes: this.cartEvents.map(i => ({
                            type_id: i.item.type_id,
                            price_id: i.priceOption.price_id,
                            qty: i.amount,
                            insurance_qty: i.insuranceAmount
                        }))
                    })
                }
            }

            // structure for tour checkout
            if (this.cartTours.length && !this.isProduct) {
                payload = {
                    ...payload,
                    start: DateTime.fromISO(this.cartTours[0].slot.date).toFormat('yyyy-MM-dd') + ' ' + this.cartTours[0].slot.time,
                    type: 'tour',
                    data: JSON.stringify({
                        ...data,
                        tour_id: this.cartTours[0].item.id,
                        bikes: this.cartTours[0].item.bike_types.filter(j => (j.amount > 0)).map(j => ({
                            type_id: j.type_id,
                            price_id: null,
                            qty: j.amount,
                            insurance_qty: j.insuranceAmount
                        }))
                    })
                }
            }

            if(this.isProduct) {
                payload = {
                    debug: process.env.NODE_ENV === 'development',
                    ref: this.referrer,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email,
                    phone: this.phone,
                    discount_code: null,
                    type: 'product',
                    shipping_method: this.productShippingMethod,
                    data: JSON.stringify({
                        products: [
                            {
                                id: this.productVariantSelected,
                                qty: 1
                            }
                        ]
                    })
                }

                if (this.productShippingMethod === 'domestic_shipping') {
                    payload = {
                        ...payload,
                        shipping_address: this.productShippingAddress
                    }
                }
            }

            return payload;
        },

        createOrder(paymentToken) {
            if(this.refContactInformation) {
                if ( !this.refContactInformation.validate()) {
                    return false;
                }
            } else {
                if ( !this.$parent.validate()) {
                    return false;
                }
            }

            this.enableLoader();
            const payload = this.createPayload('google-pay', this.productData);
            payload.paymentToken = paymentToken;

            new ApiRequest().createDigitalWalletOrder(payload).then((response) => {

                if (!response.data.status) {
                    this.errorNotify(response.data.message);
                    this.disableLoader();
                    return;
                }

                this.initCreditCardPayment();

                try {
                    gtag('event', 'conversion', {
                        'send_to': window.gtag_send_to,
                        'value': ((this.totalPrice + this.totalTax - this.totalDiscount) / 100),
                        'currency': 'USD',
                        'transaction_id': response.data.info.qr,
                        'ref': this.referrer
                    });
                } catch (e) {
                    console.error(e.message);
                }

                location.href = '/checkout-success/thank-you-' + payload.type + '/' + btoa(JSON.stringify({ data: response.data }));

            }).catch((error) => {
                this.errorNotify('');
            }).finally(() => {
                this.disableLoader();
            });

        },

        onLoadPaymentData(event) {
            const paymentToken = event.detail.paymentMethodData.tokenizationData.token;
            // console.log("load payment data", event.detail);
            this.createOrder(paymentToken);
        },

        onError(event) {
            console.error("error", event.error);
        }
    }
}
</script>

<style>
.google-pay-container {
    padding-top: 11px;
    padding-bottom: 11px;
}
</style>
